import { useMemo } from "react";

// Escape a string for use inside a RegExp pattern
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Render the clinical note with the given `highlight` substring wrapped in a <mark>.
 * Falls back to plain text if the substring is not found. Case-insensitive match,
 * only the first occurrence is highlighted. When `highlight` is empty, renders plain text.
 */
export default function HighlightedNote({ note, highlight }) {
  const parts = useMemo(() => {
    if (!highlight || !note) return [{ text: note || "", mark: false }];
    const trimmed = highlight.trim();
    if (!trimmed) return [{ text: note, mark: false }];

    // Try full match first. If not found, try the longest contiguous word-run of the evidence.
    const tryPatterns = [trimmed];
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length > 3) {
      // Try a center-ish window of words
      const mid = words.slice(1, -1).join(" ");
      if (mid.length >= 8) tryPatterns.push(mid);
    }
    if (words.length > 2) {
      tryPatterns.push(words.slice(0, Math.min(4, words.length)).join(" "));
    }

    for (const p of tryPatterns) {
      const re = new RegExp(escapeRegex(p), "i");
      const m = note.match(re);
      if (m) {
        const start = m.index;
        const end = start + m[0].length;
        return [
          { text: note.slice(0, start), mark: false },
          { text: note.slice(start, end), mark: true },
          { text: note.slice(end), mark: false },
        ];
      }
    }
    return [{ text: note, mark: false }];
  }, [note, highlight]);

  return (
    <pre
      className="w-full h-full m-0 p-4 bg-transparent resize-none outline-none text-sm leading-relaxed text-[#2C332A] font-sans whitespace-pre-wrap break-words tc-scroll overflow-y-auto"
      data-testid="highlighted-note"
      style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
    >
      {parts.map((p, i) =>
        p.mark ? (
          <mark
            key={i}
            className="bg-[#FAD8C3] text-[#2C332A] rounded px-0.5 py-[1px] ring-1 ring-[#8C3A27]/30 transition-colors"
            data-testid="note-highlight"
          >
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        )
      )}
    </pre>
  );
}
